import { useEffect } from "react";

import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";

import { useApplyFilters } from "./useApplyFilters";

import { saveAs } from "file-saver";

import { fetchExportVehicleSlice } from "../slices/exportVehiclesSlice";

import { setShowAlert } from "../slices/alertSlice";
import ExcelJS from "exceljs";


export const useVehiclesExport = ({ componentId }) => {
    const dispatch = useDispatch();
    const token = useSelector((state) => state.auth.token);
    const meta = useSelector((state) => state.exportVehicles.meta);
    const columns = useSelector((state) => state.exportVehicles.columns);

    const { params } = useApplyFilters({ componentId });

    useEffect(() => {
        if (!meta || Object.keys(meta).length === 0) {
            dispatch(fetchExportVehicleSlice({ token, params, page: 1 }));
        }
    }, [dispatch, token, params, meta]);

    const handleExport = async () => {
        if (!meta || !meta.last_page) {
            dispatch(
                setShowAlert({
                    type: "failure",
                    messages: "Ошибка, Meta не инициализирована",
                    context: "exportData"
                })
            );
            return;
        }

        const total_pages = meta.last_page;
        let currentPage = 1;
        const allData = [];

        try {
            while (currentPage <= total_pages) {
                const result = await dispatch(
                    fetchExportVehicleSlice({ token, params, page: currentPage })
                ).unwrap();

                if (!result || !result.data) {
                    console.error("Ошибка: данные экспорта отсутствуют", result);
                    return;
                }

                allData.push(...result.data);
                currentPage++;
            }

            if (!columns || !Array.isArray(columns)) {
                console.error("Ошибка: columnsExport не является массивом", columns);
                return;
            }

            const excelColumns = (() => {
                const hasImageColumn = columns.some((c) => c.key === "image_path");
                if (hasImageColumn) return columns;
                return [
                    ...columns,
                    {
                        key: "image_path",
                        name: "Фото"
                    }
                ];
            })();

            const workbook = new ExcelJS.Workbook();
            const worksheet = workbook.addWorksheet("Export");

            const originalWidths = [13.0, 18.3, 18.4, 19.2, 19.3, 22.2, 22.3];
            const defaultWidth = 30;

            worksheet.columns = excelColumns.map((col, index) => ({
                header: col.alias || col.name,
                key: col.key,
                width:
                    col.key === "image_path"
                        ? 20
                        : originalWidths[index] ?? defaultWidth,
                style: {
                    alignment: {
                        wrapText: true,
                        vertical: "top"
                    }
                }
            }));
 
            const today = new Date().toLocaleDateString("ru-RU");

            worksheet.insertRow(1, [`Дата выгрузки: ${today}`]);

            worksheet.mergeCells(1, 1, 1, excelColumns.length);

            const dateRow = worksheet.getRow(1);
            dateRow.font = { bold: true, size: 14 };
            dateRow.alignment = { vertical: "middle", horizontal: "left" };

            worksheet.insertRow(2, []);

            const HEADER_ROW_INDEX = 3;

            const headerRow = worksheet.getRow(HEADER_ROW_INDEX);
            headerRow.eachCell((cell) => {
                cell.font = { bold: true };
                cell.alignment = { wrapText: true, vertical: "top" };
            });

            allData.forEach((item) => {
                const attributes = Array.isArray(item.attributes)
                    ? item.attributes[0]
                    : item.attributes;
                const row = {};

                excelColumns.forEach((col) => {
                    const key = col.key;
                    let value = attributes[key];

                    if (key === "image_path") {
                        row[key] = value ? "" : "Отсутствует";
                        return;
                    }

                    if (Array.isArray(value)) {
                        row[key] =
                            value.length > 0 ? value.join(", ") : "Отсутствует";
                    } else {
                        row[key] =
                            value !== null && value !== undefined ? value : "Отсутствует";
                    }
                });

                worksheet.addRow(row);
            });

            worksheet.eachRow((row, rowNumber) => {
                row.eachCell((cell) => {
                    cell.alignment = {
                        wrapText: true,
                        vertical: "top"
                    };

                    if (rowNumber >= HEADER_ROW_INDEX) {
                        cell.border = {
                            top: { style: "thin" },
                            left: { style: "thin" },
                            bottom: { style: "thin" },
                            right: { style: "thin" }
                        };
                    }
                });
            });

            const imageColIndex =
                excelColumns.findIndex((c) => c.key === "image_path") + 1;

            if (imageColIndex > 0) {
                allData.forEach((item, index) => {
                    const attributes = Array.isArray(item.attributes)
                        ? item.attributes[0]
                        : item.attributes;

                    const imageUrl = attributes.image_path;

                    if (!imageUrl) {
                        return;
                    }

                    const rowIndex = HEADER_ROW_INDEX + 1 + index;
                    const cell = worksheet.getCell(rowIndex, imageColIndex);

                    cell.value = {
                        text: "Посмотреть фото",
                        hyperlink: imageUrl
                    };
                    cell.font = {
                        color: { argb: "FF0000FF" },
                        underline: true
                    };
                });
            }

            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            });
            saveAs(blob, "Vehicles.xlsx");
        } catch (error) {
            console.error(error);
            dispatch(
                setShowAlert({
                    type: "failure",
                    messages: error.message,
                    context: "exportData"
                })
            );
        }
    };

    return { handleExport };
};